/*!
 * roomLink.js — Oscilla Live in a room, through a relay (server/liveRelay.js)
 * © 2026 Rob Canning — GPLv3
 *
 * joinRoom() opens the relay and joins the room; from then on the relay
 * is the room for the console, as the score's server is on index.html:
 * window.socket for what goes out (room.js), its messages in as window
 * events (roomEvents.js), its clock for the beats (serverClock.js), so
 * every performer's quant: lands on the same beat. A dropped connection is
 * joined again by itself; a refused one (a wrong password) is not. Left or
 * refused, it says so (oscilla:room-closed).
 */

import { startServerClock, stopServerClock, onTimePong } from "./serverClock.js";
import { deliverRoomMessage, roomOpened } from "./roomEvents.js";

const RETRY_FIRST_MS = 1000;
const RETRY_MOST_MS = 15000;

/**
 * @param {object} o
 * @param {string} o.relay      ws:// or wss:// address of the relay
 * @param {string} o.room
 * @param {string} o.password
 * @param {string} [o.name]     what the others see; the relay may add -2
 * @param {(s: { state: "joining"|"in"|"reconnecting"|"refused"|"left", room?, name?, peers?, why? }) => void} [o.onStatus]
 *   told again ("in", with peers) whenever someone joins or leaves
 * @returns {{ leave(): void }}
 */
export function joinRoom({ relay, room, password, name, onStatus = () => {} }) {
  let ws = null;
  let stopped = false;
  let retryMs = RETRY_FIRST_MS;
  let timer = null;

  function connect() {
    onStatus({ state: "joining", room });
    ws = new WebSocket(relay);
    const me = ws;
    me.addEventListener("open", () => me.send(JSON.stringify({ type: "join", room, password, name })));
    me.addEventListener("message", (e) => {
      let d;
      try { d = JSON.parse(e.data); } catch { return; }
      switch (d?.type) {
        case "joined":
          retryMs = RETRY_FIRST_MS;
          name = d.name;                       // joined again later under the same name
          window.localClientName = d.name;
          window.localClientColor = d.color;
          window.socket = me;
          startServerClock(me);
          roomOpened();
          onStatus({ state: "in", room: d.room, name: d.name, peers: d.peers });
          return;
        case "room_peers":
          onStatus({ state: "in", room, name, peers: d.peers });
          return;
        case "join_refused":
          stopped = true;
          onStatus({ state: "refused", room, why: d.why });
          return;
        case "time_pong":
          onTimePong(d);
          return;
        default:
          deliverRoomMessage(d);
      }
    });
    me.addEventListener("close", () => {
      if (window.socket === me) { window.socket = null; stopServerClock(); }
      // left, or refused: no room now (liveClockLink.js joins the clock alone again)
      if (stopped) { window.dispatchEvent(new Event("oscilla:room-closed")); return; }
      onStatus({ state: "reconnecting", room });
      timer = setTimeout(connect, retryMs);
      retryMs = Math.min(retryMs * 2, RETRY_MOST_MS);
    });
  }

  connect();
  return {
    leave() {
      stopped = true;
      clearTimeout(timer);
      ws?.close();
      onStatus({ state: "left", room });
    },
  };
}
